<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import BrandsAPI from '../../../../api/brands';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();

const inboxes = useMapGetter('inboxes/getInboxes');

const brands = ref([]);
const isLoading = ref(true);
const loadError = ref('');
const formError = ref('');
const isSaving = ref(false);

// The brand currently open in the editor. `null` = the list view; an object
// with no id = the "add brand" form.
const editing = ref(null);

// Literal translation keys rather than interpolated ones, so missing strings
// are caught statically (and the i18n linter stays quiet).
const providerOptions = computed(() => [
  {
    value: 'inherit',
    label: t('INTEGRATION_SETTINGS.BRANDS.PROVIDERS.INHERIT'),
    hint: t('INTEGRATION_SETTINGS.BRANDS.PROVIDER_HINTS.INHERIT'),
  },
  {
    value: 'ellie_server',
    label: t('INTEGRATION_SETTINGS.BRANDS.PROVIDERS.ELLIE_SERVER'),
    hint: t('INTEGRATION_SETTINGS.BRANDS.PROVIDER_HINTS.ELLIE_SERVER'),
  },
  {
    value: 'ellie_docs',
    label: t('INTEGRATION_SETTINGS.BRANDS.PROVIDERS.ELLIE_DOCS'),
    hint: t('INTEGRATION_SETTINGS.BRANDS.PROVIDER_HINTS.ELLIE_DOCS'),
  },
  {
    value: 'none',
    label: t('INTEGRATION_SETTINGS.BRANDS.PROVIDERS.NONE'),
    hint: t('INTEGRATION_SETTINGS.BRANDS.PROVIDER_HINTS.NONE'),
  },
]);

const providerLabel = value =>
  providerOptions.value.find(option => option.value === value)?.label || value;

const currentProviderHint = computed(
  () =>
    providerOptions.value.find(
      option => option.value === editing.value?.knowledgeProvider
    )?.hint || ''
);

const fetchBrands = async () => {
  try {
    isLoading.value = true;
    loadError.value = '';
    const { data } = await BrandsAPI.get();
    brands.value = data.brands || [];
  } catch (error) {
    loadError.value =
      error.response?.data?.error ||
      t('INTEGRATION_SETTINGS.BRANDS.LOAD_ERROR');
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchBrands);

// Inboxes already claimed by a *different* brand — shown as disabled, since
// an inbox belongs to at most one brand (enforced in the database too).
const claimedElsewhere = computed(() => {
  const claimed = new Map();
  brands.value.forEach(brand => {
    if (brand.id === editing.value?.id) return;
    (brand.inboxIds ?? []).forEach(id => claimed.set(id, brand.name));
  });
  return claimed;
});

const inboxNameFor = id =>
  inboxes.value.find(i => i.id === id)?.name || `#${id}`;

const startCreate = () => {
  formError.value = '';
  editing.value = {
    name: '',
    knowledgeProvider: 'inherit',
    knowledgeConfig: {},
    brandVoice: '',
    language: '',
    inboxIds: [],
  };
};

const startEdit = brand => {
  formError.value = '';
  editing.value = {
    ...brand,
    brandVoice: brand.brandVoice || '',
    language: brand.language || '',
    knowledgeConfig: { ...(brand.knowledgeConfig || {}) },
    inboxIds: [...(brand.inboxIds || [])],
  };
};

const cancelEdit = () => {
  editing.value = null;
  formError.value = '';
};

const toggleInbox = id => {
  if (claimedElsewhere.value.has(id)) return;
  const selected = editing.value.inboxIds;
  const index = selected.indexOf(id);
  if (index === -1) selected.push(id);
  else selected.splice(index, 1);
};

const configField = (key, value) => {
  editing.value.knowledgeConfig = {
    ...editing.value.knowledgeConfig,
    [key]: value,
  };
};

const handleSave = async () => {
  const brand = editing.value;
  if (!brand.name.trim()) {
    formError.value = t('INTEGRATION_SETTINGS.BRANDS.FORM.NAME_REQUIRED');
    return;
  }

  const payload = {
    name: brand.name.trim(),
    knowledgeProvider: brand.knowledgeProvider,
    knowledgeConfig: brand.knowledgeConfig,
    // Empty clears the override and falls back to the account default.
    brandVoice: brand.brandVoice.trim() || null,
    language: brand.language.trim() || null,
  };

  try {
    formError.value = '';
    isSaving.value = true;
    const { data } = brand.id
      ? await BrandsAPI.update(brand.id, payload)
      : await BrandsAPI.create(payload);
    // Remember the id before the second call. If setInboxes then fails, the
    // brand already exists — without this, retrying Save would POST again and
    // create a duplicate.
    brand.id = data.brand.id;
    // Inboxes are a separate call so a rejected mapping (e.g. an id from
    // another account) cannot leave the brand itself half-saved.
    await BrandsAPI.setInboxes(brand.id, brand.inboxIds);
    await fetchBrands();
    editing.value = null;
  } catch (error) {
    formError.value = error.response?.data?.error || error.message;
  } finally {
    isSaving.value = false;
  }
};

const handleDelete = async brand => {
  if (
    // eslint-disable-next-line no-alert
    !window.confirm(
      t('INTEGRATION_SETTINGS.BRANDS.DELETE_CONFIRM', { name: brand.name })
    )
  ) {
    return;
  }
  try {
    formError.value = '';
    await BrandsAPI.delete(brand.id);
    await fetchBrands();
  } catch (error) {
    formError.value = error.response?.data?.error || error.message;
  }
};
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.BRANDS.HEADER')"
        :description="$t('INTEGRATION_SETTINGS.BRANDS.DESCRIPTION')"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6 max-w-2xl">
        <p v-if="loadError" class="text-sm text-n-ruby-9">{{ loadError }}</p>
        <p v-if="formError" class="text-sm text-n-ruby-9">{{ formError }}</p>

        <!-- List -->
        <template v-if="!editing">
          <div
            v-if="!isLoading && !brands.length"
            class="p-4 rounded-md bg-n-slate-2 text-sm text-n-slate-11"
          >
            {{ $t('INTEGRATION_SETTINGS.BRANDS.EMPTY') }}
          </div>

          <div
            v-for="brand in brands"
            :key="brand.id"
            class="flex items-start justify-between p-4 rounded-md bg-n-slate-2 gap-4"
          >
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ brand.name }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{
                  $t('INTEGRATION_SETTINGS.BRANDS.KNOWLEDGE_LABEL', {
                    provider: providerLabel(brand.knowledgeProvider),
                  })
                }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{
                  brand.inboxIds?.length
                    ? brand.inboxIds.map(inboxNameFor).join(', ')
                    : $t('INTEGRATION_SETTINGS.BRANDS.NO_INBOXES')
                }}
              </span>
            </div>
            <div class="flex gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                :label="$t('INTEGRATION_SETTINGS.BRANDS.EDIT_BUTTON')"
                @click="startEdit(brand)"
              />
              <Button
                variant="outline"
                color="ruby"
                size="sm"
                :label="$t('INTEGRATION_SETTINGS.BRANDS.DELETE_BUTTON')"
                @click="handleDelete(brand)"
              />
            </div>
          </div>

          <div>
            <Button
              :label="$t('INTEGRATION_SETTINGS.BRANDS.ADD_BUTTON')"
              @click="startCreate"
            />
          </div>
        </template>

        <!-- Editor -->
        <template v-else>
          <Input
            v-model="editing.name"
            :label="$t('INTEGRATION_SETTINGS.BRANDS.FORM.NAME_LABEL')"
            :placeholder="
              $t('INTEGRATION_SETTINGS.BRANDS.FORM.NAME_PLACEHOLDER')
            "
          />

          <div class="w-full">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.BRANDS.FORM.KNOWLEDGE_LABEL') }}
              <select v-model="editing.knowledgeProvider">
                <option
                  v-for="option in providerOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
            <p class="text-sm text-n-slate-11">{{ currentProviderHint }}</p>
          </div>

          <Input
            v-if="editing.knowledgeProvider === 'ellie_server'"
            :model-value="editing.knowledgeConfig.userUuid || ''"
            :label="$t('INTEGRATION_SETTINGS.BRANDS.FORM.USER_UUID_LABEL')"
            @update:model-value="configField('userUuid', $event)"
          />
          <Input
            v-if="editing.knowledgeProvider === 'ellie_docs'"
            :model-value="editing.knowledgeConfig.baseUrl || ''"
            :label="$t('INTEGRATION_SETTINGS.BRANDS.FORM.HELP_CENTRE_LABEL')"
            :placeholder="
              $t('INTEGRATION_SETTINGS.BRANDS.FORM.HELP_CENTRE_PLACEHOLDER')
            "
            @update:model-value="configField('baseUrl', $event)"
          />

          <Input
            v-model="editing.brandVoice"
            :label="$t('INTEGRATION_SETTINGS.BRANDS.FORM.VOICE_LABEL')"
            :placeholder="
              $t('INTEGRATION_SETTINGS.BRANDS.FORM.VOICE_PLACEHOLDER')
            "
          />
          <Input
            v-model="editing.language"
            :label="$t('INTEGRATION_SETTINGS.BRANDS.FORM.LANGUAGE_LABEL')"
            :placeholder="
              $t('INTEGRATION_SETTINGS.BRANDS.FORM.LANGUAGE_PLACEHOLDER')
            "
          />

          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.BRANDS.FORM.INBOXES_LABEL') }}
            </span>
            <label
              v-for="inbox in inboxes"
              :key="inbox.id"
              class="flex items-center gap-2 text-sm"
              :class="
                claimedElsewhere.has(inbox.id)
                  ? 'text-n-slate-10'
                  : 'text-n-slate-12'
              "
            >
              <input
                type="checkbox"
                :checked="editing.inboxIds.includes(inbox.id)"
                :disabled="claimedElsewhere.has(inbox.id)"
                @change="toggleInbox(inbox.id)"
              />
              {{ inbox.name }}
              <span v-if="claimedElsewhere.has(inbox.id)">
                {{
                  $t('INTEGRATION_SETTINGS.BRANDS.FORM.INBOX_CLAIMED', {
                    name: claimedElsewhere.get(inbox.id),
                  })
                }}
              </span>
            </label>
          </div>

          <div class="flex gap-2">
            <Button
              :label="$t('INTEGRATION_SETTINGS.BRANDS.FORM.SAVE_BUTTON')"
              :is-loading="isSaving"
              @click="handleSave"
            />
            <Button
              variant="outline"
              :label="$t('INTEGRATION_SETTINGS.BRANDS.FORM.CANCEL_BUTTON')"
              @click="cancelEdit"
            />
          </div>
        </template>
      </div>
    </template>
  </SettingsLayout>
</template>
